// Perfil → Manutenção do veículo no app do cliente (pedido 2026-10-14: "o
// cliente poder criar manutenção pro veículo dele, editar o km, igual na
// Gestão de Frota, mas com um menu legal pro celular"). Mesmo backend da
// Gestão de Frota (/fleet-management/maintenances, catálogo de serviços,
// km do rastreador) — o cliente "usuario" pode usar essas rotas pros
// PRÓPRIOS veículos mesmo sem o módulo (ver maintenanceScopeOf no backend).
// Repetição (a cada X km / dias) e avisos (push / WhatsApp) são os mesmos.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Vehicle } from "@/types";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { useFleetList, useFleetMaintenanceServices, useFleetMutations } from "@/hooks/queries/fleet-management";
import {
  fleetManagementService,
  type FleetMaintenance,
  type FleetMaintenanceService,
} from "@/services/gpsdrive-fleet-management.service";
import { LicensePlate } from "@/components/license-plate";

// Cliente: o backend sempre usa ele mesmo como dono.
const OWNER = "";
const nf = new Intl.NumberFormat("pt-BR");
const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
const brDate = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const daysUntil = (iso: string) => Math.round((new Date(`${iso.slice(0, 10)}T12:00:00`).getTime() - Date.now()) / 86_400_000);

function errMsg(err: unknown, fallback: string) {
  return err instanceof GpsDriveApiError ? err.message : fallback;
}

// Emoji do serviço pelo nome (catálogo é livre por empresa).
function serviceEmoji(title: string): string {
  const t = title.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  if (/oleo|lubrif/.test(t)) return "🛢️";
  if (/pneu|rodizio|calibr/.test(t)) return "🛞";
  if (/freio|pastilha|disco/.test(t)) return "🛑";
  if (/filtro/.test(t)) return "🌀";
  if (/bateria/.test(t)) return "🔋";
  if (/alinhamento|balanceamento|suspens|amortec/.test(t)) return "⚙️";
  if (/correia|motor|vela/.test(t)) return "🔩";
  if (/ar.?cond|climatiz/.test(t)) return "❄️";
  if (/revis/.test(t)) return "🧰";
  if (/lav|higien/.test(t)) return "🧽";
  return "🔧";
}

function vehicleEmoji(v: Vehicle) {
  return v.type === "moto" ? "🏍️" : v.type === "truck" ? "🚚" : v.type === "van" ? "🚐" : v.type === "machine" ? "🚜" : "🚗";
}

type Urgency = "late" | "soon" | "ok";

function urgencyOf(m: FleetMaintenance, kmNow: number | null): { level: Urgency; parts: string[] } {
  const parts: string[] = [];
  let level: Urgency = "ok";
  const bump = (l: Urgency) => {
    if (l === "late" || (l === "soon" && level === "ok")) level = l;
  };
  if (m.scheduledDate) {
    const d = daysUntil(m.scheduledDate);
    parts.push(
      d < 0 ? `📅 venceu há ${-d} dia${d === -1 ? "" : "s"}` : d === 0 ? "📅 é hoje" : `📅 ${brDate(m.scheduledDate)} · em ${d} dia${d === 1 ? "" : "s"}`,
    );
    bump(d < 0 ? "late" : d <= m.notifyDaysBefore ? "soon" : "ok");
  }
  if (m.scheduledKm != null) {
    if (kmNow != null) {
      const left = m.scheduledKm - kmNow;
      parts.push(left <= 0 ? `🛣️ passou ${nf.format(-left)} km` : `🛣️ aos ${nf.format(m.scheduledKm)} km · faltam ${nf.format(left)}`);
      bump(left <= 0 ? "late" : left <= m.notifyKmBefore ? "soon" : "ok");
    } else parts.push(`🛣️ aos ${nf.format(m.scheduledKm)} km`);
  }
  return { level, parts };
}

const URGENCY: Record<Urgency, { label: string; chip: string; bar: string }> = {
  late: { label: "Atrasada", chip: "bg-rose-500/15 text-rose-600 dark:text-rose-400", bar: "bg-rose-500" },
  soon: { label: "Em breve", chip: "bg-amber-500/15 text-amber-600 dark:text-amber-400", bar: "bg-amber-500" },
  ok: { label: "Em dia", chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
};

// ---------------------------------------------------------------------------

export function ClientMaintenanceScreen({ vehicles, onClose }: { vehicles: Vehicle[]; onClose: () => void }) {
  const mine = useMemo(() => vehicles.filter((v) => !v.isTag), [vehicles]);
  const [vehicleId, setVehicleId] = useState<string | null>(mine[0]?.id ?? null);
  useEffect(() => {
    if (!vehicleId && mine[0]) setVehicleId(mine[0].id);
  }, [mine, vehicleId]);
  const vehicle = mine.find((v) => v.id === vehicleId) ?? null;

  const { data: all = [], isLoading } = useFleetList("maintenances", OWNER);
  const { data: catalog } = useFleetMaintenanceServices(OWNER);
  const odometer = useQuery({
    queryKey: ["fleet-mgmt", "odometer", vehicleId],
    queryFn: () => fleetManagementService.odometer(OWNER, vehicleId!),
    enabled: !!vehicleId,
    staleTime: 60_000,
  });
  const kmNow = odometer.data?.km ?? (vehicle?.odometer != null ? Math.round(vehicle.odometer) : null);

  const list = all.filter((m) => String(m.traccarDeviceId) === vehicleId);
  const upcoming = list
    .filter((m) => m.status === "agendada")
    .map((m) => ({ m, u: urgencyOf(m, kmNow) }))
    .sort((a, b) => ["late", "soon", "ok"].indexOf(a.u.level) - ["late", "soon", "ok"].indexOf(b.u.level));
  const history = list
    .filter((m) => m.status === "concluida")
    .sort((a, b) => (b.doneAt ?? "").localeCompare(a.doneAt ?? ""));
  const late = upcoming.filter((x) => x.u.level === "late").length;
  const soon = upcoming.filter((x) => x.u.level === "soon").length;
  const spent = history.reduce((s, m) => s + (m.cost ?? 0), 0);

  const [sheet, setSheet] = useState<
    { kind: "form"; editing: FleetMaintenance | null } | { kind: "done"; m: FleetMaintenance } | { kind: "km" } | null
  >(null);
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-50 dark:bg-slate-950">
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">🛠️ Manutenção do veículo</div>
      </div>

      <div className="flex-1 overflow-y-auto pb-28">
        {mine.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-slate-500">Nenhum veículo vinculado à sua conta.</div>
        ) : (
          <>
            {mine.length > 1 && (
              <div className="flex gap-2 overflow-x-auto px-4 pt-4 [scrollbar-width:none]">
                {mine.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setVehicleId(v.id)}
                    className={cn(
                      "flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                      v.id === vehicleId
                        ? "border-transparent bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                        : "border-slate-200 bg-white text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200",
                    )}
                  >
                    <span aria-hidden>{vehicleEmoji(v)}</span>
                    <span className="max-w-[9rem] truncate">{v.name}</span>
                  </button>
                ))}
              </div>
            )}

            {vehicle && (
              <div className="px-4 pt-4">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 p-5 text-white shadow-lg">
                  <div className="pointer-events-none absolute -right-8 -top-10 text-[120px] opacity-10" aria-hidden>
                    {vehicleEmoji(vehicle)}
                  </div>
                  <div className="text-xs font-medium text-white/70">
                    {vehicle.name}
                    {vehicle.plate && <LicensePlate plate={vehicle.plate} size="xs" className="ml-1.5" />}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-wider text-white/60">Km atual</div>
                  <div className="flex items-end gap-2">
                    <div className="text-4xl font-extrabold tabular-nums">
                      {odometer.isLoading && kmNow == null ? "…" : kmNow != null ? nf.format(kmNow) : "—"}
                    </div>
                    <div className="pb-1 text-sm text-white/70">km</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSheet({ kind: "km" })}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold backdrop-blur active:bg-white/25"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Ajustar km
                  </button>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <Stat n={late} label="atrasada" tone={late ? "text-rose-300" : "text-white"} />
                    <Stat n={soon} label="em breve" tone={soon ? "text-amber-300" : "text-white"} />
                    <Stat n={history.length} label="feitas" tone="text-white" />
                  </div>
                </div>
              </div>
            )}

            <Section title="Próximas manutenções">
              {isLoading ? (
                <div className="grid place-items-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : upcoming.length === 0 ? (
                <button
                  type="button"
                  onClick={() => setSheet({ kind: "form", editing: null })}
                  className="w-full rounded-2xl border-2 border-dashed border-slate-300 px-4 py-8 text-center dark:border-slate-700"
                >
                  <div className="text-3xl" aria-hidden>
                    🧰
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">Nenhuma manutenção agendada</div>
                  <div className="mt-0.5 text-xs text-slate-500">Toque para agendar a primeira (troca de óleo, pneus…)</div>
                </button>
              ) : (
                <div className="space-y-3">
                  {upcoming.map(({ m, u }) => (
                    <MaintenanceCard
                      key={m.id}
                      m={m}
                      kmNow={kmNow}
                      urgency={u}
                      onOpen={() => setSheet({ kind: "form", editing: m })}
                      onDone={() => setSheet({ kind: "done", m })}
                    />
                  ))}
                </div>
              )}
            </Section>

            {history.length > 0 && (
              <Section
                title="Histórico"
                right={
                  spent > 0 ? <span className="text-xs font-semibold text-slate-500">{brl.format(spent)} gastos</span> : null
                }
              >
                <div className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-slate-900">
                  {(showHistory ? history : history.slice(0, 4)).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSheet({ kind: "form", editing: m })}
                      className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 dark:border-slate-800"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-500/10 text-lg" aria-hidden>
                        {serviceEmoji(m.title)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{m.title}</div>
                        <div className="truncate text-xs text-slate-500">
                          {[brDate(m.doneAt), m.doneKm != null ? `${nf.format(m.doneKm)} km` : null, m.provider]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      {m.cost != null && (
                        <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                          {brl.format(m.cost)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {history.length > 4 && (
                  <button
                    type="button"
                    onClick={() => setShowHistory((v) => !v)}
                    className="mx-auto mt-2 flex items-center gap-1 text-xs font-semibold text-slate-500"
                  >
                    {showHistory ? "Mostrar menos" : `Ver todas (${history.length})`}
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showHistory && "rotate-180")} />
                  </button>
                )}
              </Section>
            )}
          </>
        )}
      </div>

      {vehicle && (
        <button
          type="button"
          onClick={() => setSheet({ kind: "form", editing: null })}
          className="absolute right-4 flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3.5 text-sm font-bold text-white shadow-xl active:scale-95 dark:bg-white dark:text-slate-900"
          style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
        >
          <Plus className="h-5 w-5" /> Nova manutenção
        </button>
      )}

      {sheet?.kind === "form" && vehicle && (
        <MaintenanceForm
          vehicle={vehicle}
          kmNow={kmNow}
          catalog={catalog?.items ?? []}
          editing={sheet.editing}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === "done" && <DoneSheet m={sheet.m} kmNow={kmNow} onClose={() => setSheet(null)} />}
      {sheet?.kind === "km" && vehicle && <KmSheet vehicle={vehicle} kmNow={kmNow} onClose={() => setSheet(null)} />}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone: string }) {
  return (
    <div className="rounded-2xl bg-white/10 px-2 py-2">
      <div className={cn("text-xl font-extrabold tabular-nums", tone)}>{n}</div>
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="px-4 pt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function MaintenanceCard({
  m,
  kmNow,
  urgency,
  onOpen,
  onDone,
}: {
  m: FleetMaintenance;
  kmNow: number | null;
  urgency: { level: Urgency; parts: string[] };
  onOpen: () => void;
  onDone: () => void;
}) {
  const st = URGENCY[urgency.level];
  // Barra: quanto do intervalo de km já foi rodado até a troca.
  const progress =
    m.scheduledKm != null && m.intervalKm && kmNow != null
      ? Math.min(1, Math.max(0, (kmNow - (m.scheduledKm - m.intervalKm)) / m.intervalKm))
      : m.scheduledDate && m.intervalDays
        ? Math.min(1, Math.max(0, 1 - daysUntil(m.scheduledDate) / m.intervalDays))
        : null;
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
      <button type="button" onClick={onOpen} className="flex w-full items-start gap-3 text-left">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800" aria-hidden>
          {serviceEmoji(m.title)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-bold text-slate-900 dark:text-slate-100">{m.title}</span>
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase", st.chip)}>{st.label}</span>
          </div>
          {urgency.parts.map((p) => (
            <div key={p} className="mt-0.5 text-xs text-slate-500">
              {p}
            </div>
          ))}
          {(m.intervalKm || m.intervalDays) && (
            <div className="mt-0.5 text-[11px] text-slate-400">
              🔁 a cada {[m.intervalKm ? `${nf.format(m.intervalKm)} km` : null, m.intervalDays ? `${m.intervalDays} dias` : null]
                .filter(Boolean)
                .join(" ou ")}
            </div>
          )}
        </div>
      </button>
      {progress != null && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className={cn("h-full rounded-full transition-[width] duration-700", st.bar)} style={{ width: `${progress * 100}%` }} />
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onDone}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white active:bg-emerald-700"
        >
          <CheckCircle2 className="h-4 w-4" /> Já fiz
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <Pencil className="h-4 w-4" /> Editar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Folhas (bottom sheets)

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div className="absolute inset-0 z-50">
      <div
        className={cn("absolute inset-0 bg-black/50 transition-opacity duration-200", shown ? "opacity-100" : "opacity-0")}
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 max-h-[92%] overflow-y-auto rounded-t-3xl bg-white px-5 pt-3 shadow-2xl transition-transform duration-300 ease-out dark:bg-slate-900",
          shown ? "translate-y-0" : "translate-y-full",
        )}
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-slate-300 dark:bg-slate-700" />
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls =
  "h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] text-slate-900 outline-none focus:border-sky-500 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100";

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

const intOrNull = (s: string) => {
  const n = Number(s.replace(/\D/g, ""));
  return s.trim() && Number.isFinite(n) && n > 0 ? n : null;
};
const moneyOrNull = (s: string) => {
  const t = s.trim().replace(/\./g, "").replace(",", ".");
  const n = Number(t);
  return t && Number.isFinite(n) && n >= 0 ? n : null;
};

function Toggle({ on, onChange, label, emoji }: { on: boolean; onChange: (v: boolean) => void; label: string; emoji: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={cn(
        "flex flex-1 items-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors",
        on
          ? "border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-300"
          : "border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300",
      )}
    >
      <span aria-hidden>{emoji}</span>
      <span className="flex-1 text-left">{label}</span>
      <span
        className={cn(
          "relative h-5 w-9 rounded-full transition-colors",
          on ? "bg-sky-500" : "bg-slate-300 dark:bg-slate-600",
        )}
      >
        <span
          className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", on ? "left-[18px]" : "left-0.5")}
        />
      </span>
    </button>
  );
}

function MaintenanceForm({
  vehicle,
  kmNow,
  catalog,
  editing,
  onClose,
}: {
  vehicle: Vehicle;
  kmNow: number | null;
  catalog: FleetMaintenanceService[];
  editing: FleetMaintenance | null;
  onClose: () => void;
}) {
  const { create, update, remove } = useFleetMutations("maintenances", OWNER);
  const [title, setTitle] = useState(editing?.title ?? "");
  const [type, setType] = useState<"preventiva" | "corretiva">(editing?.type ?? "preventiva");
  const [date, setDate] = useState(editing?.scheduledDate?.slice(0, 10) ?? "");
  const [allServices, setAllServices] = useState(false);
  const [km, setKm] = useState(editing?.scheduledKm != null ? String(editing.scheduledKm) : "");
  const [repeat, setRepeat] = useState(!!(editing?.intervalKm || editing?.intervalDays));
  const [everyKm, setEveryKm] = useState(editing?.intervalKm ? String(editing.intervalKm) : "");
  const [everyDays, setEveryDays] = useState(editing?.intervalDays ? String(editing.intervalDays) : "");
  const [push, setPush] = useState(editing?.notifyPush ?? true);
  const [whats, setWhats] = useState(editing?.notifyWhatsapp ?? false);
  const [phone, setPhone] = useState(editing?.notifyPhone ?? "");
  const [provider, setProvider] = useState(editing?.provider ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [cost, setCost] = useState(editing?.cost != null ? String(editing.cost).replace(".", ",") : "");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = editing?.status === "concluida";
  const busy = create.isPending || update.isPending || remove.isPending;

  // Serviço do catálogo: já sugere o intervalo e a próxima data/km.
  const pick = (s: FleetMaintenanceService) => {
    setTitle(s.name);
    setType(s.type);
    if (s.intervalKm || s.intervalDays) {
      setRepeat(true);
      setEveryKm(s.intervalKm ? String(s.intervalKm) : "");
      setEveryDays(s.intervalDays ? String(s.intervalDays) : "");
      if (!km && s.intervalKm && kmNow != null) setKm(String(kmNow + s.intervalKm));
      if (!date && s.intervalDays) {
        const d = new Date(Date.now() + s.intervalDays * 86_400_000);
        setDate(d.toISOString().slice(0, 10));
      }
    }
  };

  const save = async () => {
    if (!title.trim()) return setError("Escolha ou escreva o serviço.");
    if (!done && !date && !intOrNull(km)) return setError("Informe a data ou o km em que deve ser feita.");
    if (whats && !phone.replace(/\D/g, "")) return setError("Informe o WhatsApp para o aviso.");
    setError(null);
    const body = {
      traccarDeviceId: Number(vehicle.id),
      title: title.trim(),
      type,
      scheduledDate: date || null,
      scheduledKm: intOrNull(km),
      intervalKm: repeat ? intOrNull(everyKm) : null,
      intervalDays: repeat ? intOrNull(everyDays) : null,
      notifyPush: push,
      notifyWhatsapp: whats,
      notifyPhone: phone.trim() || null,
      provider: provider.trim() || null,
      notes: notes.trim() || null,
      ...(done ? { cost: moneyOrNull(cost) } : {}),
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync({ ...body, status: "agendada" });
      onClose();
    } catch (err) {
      setError(errMsg(err, "Não foi possível salvar."));
    }
  };

  const del = async () => {
    if (!editing) return;
    try {
      await remove.mutateAsync(editing.id);
      onClose();
    } catch (err) {
      setError(errMsg(err, "Não foi possível excluir."));
    }
  };

  return (
    <Sheet title={editing ? (done ? "Manutenção feita" : "Editar manutenção") : "Nova manutenção"} onClose={onClose}>
      <div className="space-y-5">
        <div>
          <span className="mb-2 block text-xs font-semibold text-slate-600 dark:text-slate-300">Serviço</span>
          {catalog.length > 0 && (
            <div className="mb-2 grid grid-cols-3 gap-2">
              {(allServices ? catalog : catalog.slice(0, 12)).map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => pick(s)}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-2xl border px-1 py-2.5 text-center text-[11px] font-semibold leading-tight transition-colors",
                    title === s.name
                      ? "border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-300"
                      : "border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-200",
                  )}
                >
                  <span className="text-xl" aria-hidden>
                    {serviceEmoji(s.name)}
                  </span>
                  <span className="line-clamp-2">{s.name}</span>
                </button>
              ))}
            </div>
          )}
          {/* Serviços novos vão pro fim do catálogo e ficavam escondidos
              depois dos 12 primeiros (2026-10-19). */}
          {catalog.length > 12 && (
            <button
              type="button"
              onClick={() => setAllServices((v) => !v)}
              className="mb-2 w-full text-center text-xs font-semibold text-sky-600 dark:text-sky-400"
            >
              {allServices ? "Mostrar menos" : `Ver todos os serviços (${catalog.length})`}
            </button>
          )}
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ou escreva o serviço…" />
        </div>

        {!done && (
          <>
            <div>
              <span className="mb-2 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Quando fazer <span className="font-normal text-slate-400">(o que vier primeiro)</span>
              </span>
              <div className="grid grid-cols-2 gap-2">
                <Field label="📅 Data">
                  <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field label="🛣️ No km" hint={kmNow != null ? `Agora: ${nf.format(kmNow)} km` : undefined}>
                  <input
                    inputMode="numeric"
                    className={inputCls}
                    value={km}
                    onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))}
                    placeholder="Ex.: 50000"
                  />
                </Field>
              </div>
            </div>

            <div className="space-y-2">
              <Toggle on={repeat} onChange={setRepeat} emoji="🔁" label="Repetir este serviço" />
              {repeat && (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="A cada (km)">
                    <input
                      inputMode="numeric"
                      className={inputCls}
                      value={everyKm}
                      onChange={(e) => setEveryKm(e.target.value.replace(/\D/g, ""))}
                      placeholder="10000"
                    />
                  </Field>
                  <Field label="A cada (dias)">
                    <input
                      inputMode="numeric"
                      className={inputCls}
                      value={everyDays}
                      onChange={(e) => setEveryDays(e.target.value.replace(/\D/g, ""))}
                      placeholder="180"
                    />
                  </Field>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <span className="block text-xs font-semibold text-slate-600 dark:text-slate-300">Me avisar quando estiver chegando</span>
              <div className="flex gap-2">
                <Toggle on={push} onChange={setPush} emoji="🔔" label="No app" />
                <Toggle on={whats} onChange={setWhats} emoji="💬" label="WhatsApp" />
              </div>
              {whats && (
                <input
                  inputMode="tel"
                  className={inputCls}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(51) 99999-0000"
                />
              )}
            </div>
          </>
        )}

        <Field label="🏪 Oficina (opcional)">
          <input className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Nome da oficina" />
        </Field>
        {done && (
          <Field label="💰 Quanto custou">
            <input inputMode="decimal" className={inputCls} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0,00" />
          </Field>
        )}
        <Field label="📝 Observações (opcional)">
          <textarea
            className={cn(inputCls, "h-20 py-2")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex.: óleo 5W30, trocar filtro junto"
          />
        </Field>

        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}

        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 text-[15px] font-bold text-white active:scale-[.99] disabled:opacity-60 dark:bg-white dark:text-slate-900"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {editing ? "Salvar" : "Agendar manutenção"}
        </button>
        {editing &&
          (confirmDelete ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={del}
                disabled={busy}
                className="h-11 flex-1 rounded-2xl bg-rose-600 text-sm font-bold text-white disabled:opacity-60"
              >
                Excluir de vez
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="h-11 flex-1 rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="mx-auto flex items-center gap-1.5 text-sm font-semibold text-rose-600"
            >
              <Trash2 className="h-4 w-4" /> Excluir manutenção
            </button>
          ))}
      </div>
    </Sheet>
  );
}

// "Já fiz": marca como concluída. Se repete, o servidor já agenda a próxima.
function DoneSheet({ m, kmNow, onClose }: { m: FleetMaintenance; kmNow: number | null; onClose: () => void }) {
  const { update } = useFleetMutations("maintenances", OWNER);
  const [date, setDate] = useState(today());
  const [km, setKm] = useState(kmNow != null ? String(kmNow) : "");
  const [cost, setCost] = useState("");
  const [provider, setProvider] = useState(m.provider ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: m.id,
        body: { status: "concluida", doneAt: date || today(), doneKm: intOrNull(km), cost: moneyOrNull(cost), provider: provider.trim() || null },
      });
      onClose();
    } catch (err) {
      setError(errMsg(err, "Não foi possível salvar."));
    }
  };

  return (
    <Sheet title={`${serviceEmoji(m.title)} ${m.title} — feita!`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <Field label="📅 Quando">
            <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="🛣️ Com quantos km">
            <input inputMode="numeric" className={inputCls} value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="💰 Custo">
            <input inputMode="decimal" className={inputCls} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0,00" />
          </Field>
          <Field label="🏪 Oficina">
            <input className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)} />
          </Field>
        </div>
        {(m.intervalKm || m.intervalDays) && (
          <p className="rounded-xl bg-sky-500/10 px-3 py-2 text-xs text-sky-700 dark:text-sky-300">
            🔁 A próxima já fica agendada automaticamente
            {m.intervalKm ? ` para daqui a ${nf.format(m.intervalKm)} km` : ""}
            {m.intervalKm && m.intervalDays ? " ou" : ""}
            {m.intervalDays ? ` ${m.intervalDays} dias` : ""}.
          </p>
        )}
        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
        <button
          type="button"
          onClick={save}
          disabled={update.isPending}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-[15px] font-bold text-white disabled:opacity-60"
        >
          {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
          Marcar como feita
        </button>
      </div>
    </Sheet>
  );
}

// Ajustar km: grava no rastreamento (vale a partir de agora; o rastreador
// continua somando dali). Rastreador com odômetro próprio (OBD) mantém o dele.
function KmSheet({ vehicle, kmNow, onClose }: { vehicle: Vehicle; kmNow: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [km, setKm] = useState(kmNow != null ? String(kmNow) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const n = intOrNull(km);
    if (n == null) return setError("Informe o km que aparece no painel do veículo.");
    setSaving(true);
    setError(null);
    try {
      await fleetManagementService.setOdometer(OWNER, vehicle.id, { km: n });
      qc.setQueryData(["fleet-mgmt", "odometer", vehicle.id], { deviceId: Number(vehicle.id), km: n });
      onClose();
    } catch (err) {
      setError(errMsg(err, "Não foi possível ajustar o km."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title="Ajustar km do veículo" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Olhe o km no painel do <b className="text-slate-700 dark:text-slate-200">{vehicle.name}</b> e digite aqui. Daqui pra frente o
          rastreador soma a partir desse número.
        </p>
        <input
          inputMode="numeric"
          autoFocus
          className={cn(inputCls, "h-16 text-center text-3xl font-extrabold tabular-nums")}
          value={km ? nf.format(Number(km)) : ""}
          onChange={(e) => setKm(e.target.value.replace(/\D/g, "").slice(0, 7))}
          placeholder="0"
        />
        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 text-[15px] font-bold text-white disabled:opacity-60 dark:bg-white dark:text-slate-900"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar km
        </button>
      </div>
    </Sheet>
  );
}
